/**
 * Comparison initializer for loading comparison from URL parameters
 * Handles cover_ids and policy_ids to initialize the comparison screen
 * Autoload is only enabled when autoload=1
 */

import { message } from 'antd'
import { AppDispatch } from 'store'
import { IRootState } from 'store'
import {
  fetchPoliciesAsync,
  fetchFeaturesAsync,
  fetchComparisonOptionsAsync,
  fetchPolicyContentByIdAsync,
  selectProductClassCover,
  removeAllSelections,
  setCompareBy,
  fetchClassesAsync,
} from 'slices/comparison'
import { fetchPolicyById } from 'slices/comparison/api'
import { CompareBy, ComparisonColumn, UserFeatures } from 'types/enums'
import { ISelectedClassCover, IPolicy, IClassLine, IUser } from 'types/interfaces'
import { parseUrlParams, cleanUrlParams } from './url-params'

export interface ComparisonInitializerOptions {
  dispatch: AppDispatch
  getState: () => IRootState
  searchParams: URLSearchParams
}

function hasAutoLoadFeature(userAccount: IUser | null | undefined): boolean {
  const features = userAccount?.features
  return Array.isArray(features) && features.includes(UserFeatures.AUTO_LOAD)
}

/**
 * Validate URL parameters
 */
function validateUrlParams(params: ReturnType<typeof parseUrlParams>): boolean {
  if (!params) {
    return false
  }

  // Validate cover IDs
  if (params.coverIds.length === 0) {
    message.error('At least one cover_id is required')
    return false
  }

  // Validate policy IDs (optional, but if provided, should be valid)
  if (params.policyIds.length > ComparisonColumn.MAX_SELECTED_LENGTH) {
    const maxPolicies = ComparisonColumn.MAX_SELECTED_LENGTH
    message.warning(
      `Maximum ${maxPolicies} policies allowed. Using first ${maxPolicies} policies.`,
    )
    params.policyIds = params.policyIds.slice(0, maxPolicies)
  }

  return true
}

/**
 * Initialize comparison from URL parameters
 * Main entry point for comparison initialization
 */
export async function initializeComparison({
  dispatch,
  getState,
  searchParams,
}: ComparisonInitializerOptions): Promise<boolean> {
  try {
    const userAccount = getState().auth.account
    if (!hasAutoLoadFeature(userAccount)) {
      return false
    }

    // Parse and validate URL parameters
    const params = parseUrlParams(searchParams)
    if (!params || !validateUrlParams(params)) {
      return false
    }

    const state = getState()

    // Step 1: Ensure product classes are loaded
    let productClasses = state.comparison.productClasses
    if (!productClasses || productClasses.length === 0) {
      const userAccount = state.auth.account
      const countryCode =
        userAccount?.buy_group?.country_code?.toLowerCase() ||
        state.comparison.impersonation?.selectedCountry?.toLowerCase() ||
        'au'

      const classesResult = await dispatch(fetchClassesAsync(countryCode))
      productClasses = (classesResult.payload as typeof productClasses) || []

      if (!productClasses || productClasses.length === 0) {
        message.error('Failed to load product classes. Please try again.')
        return false
      }
    }

    // Step 2: Find cover IDs and detect class
    const uniqueCoverIds = Array.from(new Set(params.coverIds))
    const allClasses = productClasses.map((classLine: IClassLine) => classLine.classes).flat()

    // Build cover-to-class map for efficient lookup
    const coverToClassMap = new Map<number, typeof allClasses[0]>()
    for (const classItem of allClasses) {
      for (const cover of classItem.covers) {
        coverToClassMap.set(cover.id, classItem)
      }
    }

    // Find valid covers and detect class in a single pass
    const covers: Array<{ id: number; name: string }> = []
    const validCoverIds: number[] = []
    let detectedClass: typeof allClasses[0] | null = null
    let classCoverCount = 0

    for (const coverId of uniqueCoverIds) {
      const classItem = coverToClassMap.get(coverId)
      if (classItem) {
        const cover = classItem.covers.find((c) => c.id === coverId)
        if (cover) {
          covers.push(cover)
          validCoverIds.push(coverId)
          
          // Detect class: track if all covers belong to the same class
          if (!detectedClass) {
            detectedClass = classItem
            classCoverCount = 1
          } else if (detectedClass.id === classItem.id) {
            classCoverCount += 1
          } else {
            // Covers span multiple classes - reset detection
            detectedClass = null
          }
        }
      }
    }

    if (covers.length === 0) {
      return false
    }

    // Warn about missing covers
    if (covers.length !== uniqueCoverIds.length) {
      const missingIds = uniqueCoverIds.filter((id) => !validCoverIds.includes(id))
      message.warning(`Some cover IDs were not found: ${missingIds.join(', ')}. Using only valid covers.`)
    }

    // Verify all valid covers belong to the detected class
    if (!detectedClass || classCoverCount !== validCoverIds.length) {
      message.error(`Could not determine class from cover IDs: ${validCoverIds.join(', ')}. Covers may span multiple classes.`)
      return false
    }

    // Step 3: Initialize comparison state with detected class and covers
    const selectedProductClassCover: ISelectedClassCover = {
      classId: detectedClass.id,
      className: detectedClass.name,
      coverId: validCoverIds,
      coverType: covers.map((c) => c.name),
    }

    await dispatch(removeAllSelections())
    dispatch(selectProductClassCover(selectedProductClassCover))
    dispatch(setCompareBy(CompareBy.COMPANY))

    // Step 4: After selecting class/covers, fetch comparison options and sections
    await Promise.all([
      dispatch(fetchComparisonOptionsAsync(validCoverIds)),
      dispatch(fetchFeaturesAsync(validCoverIds)),
    ])

    // Step 5: Fetch policies for the selected covers
    const effectiveDate = getState().comparison.filter.effectiveDate
    const policiesAction = await dispatch(
      fetchPoliciesAsync({ cover_id: validCoverIds, date: effectiveDate }),
    )

    const fetchedPolicies = (policiesAction.payload as IPolicy[]) || []

    if (fetchedPolicies.length === 0) {
      return false
    }

    // Step 6: Select policy_ids from URL - search by policy.id/section_id, or fetch by id and match proxy_id
    if (params.policyIds.length > 0) {
      const uniquePolicyIds = Array.from(new Set(params.policyIds)).slice(
        0,
        ComparisonColumn.MAX_SELECTED_LENGTH,
      )

      const findPolicyLocal = (policyId: number): IPolicy | undefined =>
        fetchedPolicies.find((p) => p.id === policyId) ??
        fetchedPolicies.find((p) =>
          p.sections?.some((s) => s.section_id === policyId),
        )

      const selectedPolicies: IPolicy[] = []
      for (const policyId of uniquePolicyIds) {
        let matched = findPolicyLocal(policyId)
        if (!matched) {
          try {
            const policyDetails = await fetchPolicyById(policyId)
            matched = fetchedPolicies.find(
              (p) => p.proxy_id === policyDetails.proxy_id,
            )
          } catch {
            // fetchPolicyById throws on error, skip this policy
          }
        }
        if (matched) selectedPolicies.push(matched)
      }

      if (selectedPolicies.length === 0) {
        message.error('No matching policies found for the provided policy IDs')
        return false
      }

      for (let index = 0; index < selectedPolicies.length; index += 1) {
        await dispatch(fetchPolicyContentByIdAsync({ policy: selectedPolicies[index], slot: index }))
      }
    }

    // Clean URL parameters after successful initialization (this acts as "already processed" check)
    cleanUrlParams()
    message.success('Comparison initialized successfully')
    return true
  } catch (error: unknown) {
    console.error('Comparison initialization error:', error)
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    message.error(`Failed to initialize comparison: ${errorMessage}`)
    // Do not clean URL on error so the user can retry (e.g. after fresh classes load)
    return false
  }
}

/**
 * Check if autoload URL parameters are present
 */
export function hasUrlParams(
  searchParams: URLSearchParams,
  userAccount: IUser | null | undefined,
): boolean {
  if (!hasAutoLoadFeature(userAccount)) {
    return false
  }

  const shouldAutoload = searchParams.get('autoload') === '1'
  const hasComparisonParams = searchParams.has('cover_ids') || searchParams.has('policy_ids')
  return shouldAutoload && hasComparisonParams
}
